/**
 * appt-findings — what the Appointments report has FOUND, in the shared card
 * grammar (./findings-kit).
 *
 * Every number is derived from the SAME getAppointmentsReport response the
 * screen already reads. Nothing here needs a new op, which is why these six
 * could ship while the three asks in the changelog are still open.
 *
 * The baseline is the LOCATION'S OWN rate, not an absolute target: a 14%
 * no-show rate is unreadable on its own, and "worse than the desks around it"
 * is a claim the data can actually support. Period-over-period would be the
 * better baseline and is an ask — `getAppointmentsReport` takes a range preset,
 * not a shifted window, so a prior period cannot be requested today.
 *
 * `booked` is whatever the contract means by it; the method line says so rather
 * than the screen asserting a definition it cannot verify.
 */
import { share, sev, capAt, type FindingVerb, type FindingCardModel } from "./findings-kit";
import type { AppointmentsReport, ApptReportRow } from "../appointments/appointments-api";
import { t } from "@v0y4ge/i18n-runtime";

/** A service's no-show rate has to clear the location's own rate by this much
 *  before it is a finding rather than noise. */
export const NOSHOW_MARGIN = 1.5;
/** Below this, a service's rates are arithmetic on too few bookings to read. */
export const MIN_BOOKED = 10;

export interface ApptFinding extends FindingCardModel {
  /** Service or staff rows this finding is about — the card filter's target. */
  rowsAffected: ApptReportRow[];
  kind: "service" | "staff" | "none";
  verb: FindingVerb | null;
}

const plural = (n: number, w: string) => `${n} ${w}${n === 1 ? "" : "s"}`;
const pct = (n: number) => `${Math.round(n * 100)}%`;
const rate = (r: ApptReportRow) => share(r.noShow, r.booked);
const keptRate = (r: ApptReportRow) => share(r.kept, r.booked);

/**
 * @param d           the report response
 * @param rangeLabel  the window, for the method lines
 * @param staffNames  false when the reader's role does not pass the staff gate —
 *                    the copy is then written without the names
 */
export function apptFindings(d: AppointmentsReport, rangeLabel: string, staffNames = true): ApptFinding[] {
  const out: ApptFinding[] = [];
  const k = d.kpis;
  const svc = d.byService ?? [];
  const staff = d.byStaff ?? [];
  const readable = svc.filter((s) => s.booked >= MIN_BOOKED);
  const houseRate = share(k.noShow, k.booked);
  const small = k.booked > 0 && k.booked < 40 ? `On ${plural(k.booked, "booking")} — too few to be conclusive.` : null;
  const cap = (s: ReturnType<typeof sev>) => capAt(s, k.booked > 0 && k.booked < 40);
  const win = rangeLabel.toLowerCase();
  const anon = (i: number) => `Service ${String.fromCharCode(65 + i)}`;
  const staffName = (s: ApptReportRow, i: number) => (staffNames ? s.name : `Advisor ${String.fromCharCode(65 + i)}`);

  /* 1 · Services missing more than the desk around them ───────────────── */
  const worse = readable.filter((s) => rate(s) > houseRate * NOSHOW_MARGIN).sort((a, b) => rate(b) - rate(a));
  const lead = worse[0];
  const worseShare = share(worse.length, readable.length || 1);
  out.push({
    id: "svc-noshow", index: "1",
    value: readable.length ? String(worse.length) : "—",
    unit: readable.length ? `of ${plural(readable.length, "service")}` : `no service reaches ${MIN_BOOKED} bookings`,
    headline: "miss far more than the rest",
    severity: !readable.length ? "unreadable" : cap(sev(worseShare >= 0.3, worse.length > 0, worse.length > 0)), note: readable.length ? small : null,
    category: "outreach",
    read: !readable.length
      ? `No service in this window has ${MIN_BOOKED} bookings or more, so a per-service rate would be arithmetic rather than a finding. Read the totals above and come back with a wider window.`
      : !lead
      ? `Every service is within half again of the ${pct(houseRate)} no-show rate across this location. Missed appointments here are spread evenly, which points at the reminder or the lead time rather than at any one desk.`
      : `${lead.name} misses ${pct(rate(lead))} against ${pct(houseRate)} across this location — ${(rate(lead) / (houseRate || 1)).toFixed(1)}× the rate its neighbours run at. A whole service missing more than its peers is usually the booking lead time or the reminder, not the students.`,
    impact: lead
      ? `${worse.reduce((a, s) => a + s.noShow, 0)} missed appointments sit in ${plural(worse.length, "service")}; ${lead.noShow} in ${lead.name} alone.`
      : "None beyond the house rate.",
    action: lead
      ? "Raise the reminder cadence on these services first, then look at how far ahead they are booked."
      : "Nothing to route per service; a reminder change would apply across the board.",
    owner: "Service owner, with advising ops.",
    method: `No-show ÷ booked per service, against the same ratio for the whole location (${pct(houseRate)}), ${win}. Services under ${MIN_BOOKED} bookings are excluded. "Booked" is the contract's own count.`,
    rows: worse.slice(0, 3).map((s, i) => ({ k: staffNames ? s.name : anon(i), v: `${pct(rate(s))} · ${s.noShow} of ${s.booked}` })),
    rowsAffected: worse, kind: "service",
    verb: lead ? { id: "reminder-review", label: t("reports.apptFindings.routeToServiceOwner", "Route {count} to the service owner", { count: worse.length }), kind: "handoff", done: t("reports.apptFindings.routedToServiceOwner", "Routed to the service owner") } : null,
  });

  /* 2 · Where the funnel loses people ────────────────────────────────── */
  const f = d.funnel ?? [];
  let dropIdx = -1, dropN = 0;
  for (let i = 1; i < f.length; i++) {
    const lost = f[i - 1].count - f[i].count;
    if (lost > dropN) { dropN = lost; dropIdx = i; }
  }
  const first = f[0]?.count ?? 0;
  const dropShare = share(dropN, first);
  out.push({
    id: "funnel", index: "2",
    value: dropIdx > 0 ? dropN.toLocaleString() : "—",
    unit: dropIdx > 0 ? `lost at “${f[dropIdx].stage}”` : "no stage loses more than another",
    headline: "the biggest drop",
    severity: dropIdx > 0 ? cap(sev(dropShare >= 0.3, dropShare >= 0.15, dropN > 0)) : "clear", note: small,
    category: "schedule",
    read: dropIdx < 0
      ? "The lifecycle stages are level in this window — nothing is leaking at one point, so improvement here is a whole-journey question rather than a fix at one step."
      : `${pct(dropShare)} of everything that entered the funnel falls out between “${f[dropIdx - 1].stage}” and “${f[dropIdx].stage}”. That is the one step worth instrumenting before changing anything else.`,
    impact: dropIdx > 0 ? `${dropN.toLocaleString()} of ${first.toLocaleString()} never reached “${f[dropIdx].stage}”.` : "None.",
    action: dropIdx > 0
      ? `Work the step before “${f[dropIdx].stage}” — what the student is asked for there is where they stop.`
      : "Nothing to route.",
    owner: "Advising ops, with whoever owns the booking journey.",
    method: `Largest absolute fall between adjacent lifecycle stages as the report returns them, ${win}.`,
    rows: f.map((x) => ({ k: x.stage, v: x.count.toLocaleString() })),
    rowsAffected: [], kind: "none",
    verb: null,
  });

  /* 3 · How it was booked ─────────────────────────────────────────────
   * The supply-side twin of Campus Pulse's booking-friction card: if staff are
   * taking the bookings, the online journey is not doing its job. */
  const byO = d.byOrigin ?? [];
  const total = byO.reduce((a, o) => a + o.count, 0);
  const selfServe = byO.filter((o) => o.origin === "journey").reduce((a, o) => a + o.count, 0);
  const staffLed = byO.filter((o) => o.origin === "staff" || o.origin === "concierge" || o.origin === "book").reduce((a, o) => a + o.count, 0);
  const selfShare = share(selfServe, total);
  out.push({
    id: "origin", index: "3",
    value: total ? pct(selfShare) : "—", unit: "booked by the student themselves",
    headline: "self-service share",
    severity: !total ? "unreadable" : cap(sev(selfShare < 0.25, selfShare < 0.5, true)), note: small,
    category: "schedule",
    read: !total
      ? "The report returned no booking origins for this window, so where these appointments came from cannot be read."
      : selfShare < 0.5
      ? `Staff are taking ${pct(share(staffLed, total))} of these bookings. Every one of those is a student who wanted a time and needed a person to get it — the same friction the engagement report sees from the student's side.`
      : `Most of these were booked by the student without help. The online journey is carrying the load; staff time is going to the appointments themselves rather than to arranging them.`,
    impact: total
      ? `${staffLed.toLocaleString()} staff-taken bookings in this window against ${selfServe.toLocaleString()} self-service.`
      : "Not measurable.",
    action: selfShare < 0.5
      ? "Test the booking journey end to end for the services with the most staff-taken bookings before adding staff hours."
      : "Hold the journey as it is; watch the share rather than the count.",
    owner: "Whoever owns the booking journey.",
    method: `Journey-origin bookings ÷ all bookings with an origin recorded, ${win}. Staff-led counts staff, concierge and day-book origins together.`,
    rows: [...byO].sort((a, b) => b.count - a.count).slice(0, 4).map((o) => ({ k: o.origin, v: o.count.toLocaleString() })),
    rowsAffected: [], kind: "none",
    verb: null,
  });

  /* 4 · Reschedule churn ─────────────────────────────────────────────── */
  const churn = k.rescheduled + k.cancelled;
  const churnShare = share(churn, k.booked);
  out.push({
    id: "churn", index: "4",
    value: churn.toLocaleString(), unit: `of ${plural(k.booked, "booking")}`,
    headline: "moved or cancelled",
    severity: cap(sev(churnShare >= 0.25, churnShare >= 0.12, churn > 0)), note: small,
    category: "schedule",
    read: !churn
      ? "Nothing in this window was moved or cancelled. Whatever the no-show rate says, the slots that were held stayed held."
      : `${pct(churnShare)} of bookings were moved or cancelled. This never appears as a no-show, but each one held a slot somebody else could have used — and a reschedule that lands is a success, so the number is a cost to watch rather than a failure to fix.`,
    impact: churn ? `${k.rescheduled.toLocaleString()} rescheduled and ${k.cancelled.toLocaleString()} cancelled slots.` : "None.",
    action: churn
      ? "Compare against the booking lead time before treating it as a problem: slots booked weeks out move more, and that is normal."
      : "Nothing to route.",
    owner: "Advising ops.",
    method: `(Rescheduled + cancelled) ÷ booked, ${win}. Both counts come from the report; a reschedule is counted once, not as a cancel plus a booking.`,
    rows: [
      { k: "Rescheduled", v: k.rescheduled.toLocaleString() },
      { k: "Cancelled", v: k.cancelled.toLocaleString() },
      { k: "Kept", v: k.kept.toLocaleString() },
    ],
    rowsAffected: [], kind: "none",
    verb: null,
  });

  /* 5 · Kept-rate spread across staff ─────────────────────────────────
   * WORKLOAD evidence, not a performance rating: whether an appointment is kept
   * is mostly the student's action and the reminder's, so this card is written
   * to send the reader looking at caseload and slot times, never at a person. */
  const rankableStaff = staff.filter((s) => s.booked >= MIN_BOOKED).sort((a, b) => keptRate(b) - keptRate(a));
  const hi = rankableStaff[0], lo = rankableStaff[rankableStaff.length - 1];
  const spread = hi && lo ? keptRate(hi) - keptRate(lo) : 0;
  out.push({
    id: "staff-spread", index: "5",
    value: rankableStaff.length >= 2 ? `${Math.round(spread * 100)} pts` : "—",
    unit: rankableStaff.length >= 2 ? "between the widest and the narrowest" : `fewer than two staff with ${MIN_BOOKED}+ bookings`,
    headline: "kept-rate spread",
    severity: rankableStaff.length < 2 ? "unreadable" : cap(sev(spread >= 0.25, spread >= 0.12, spread > 0)), note: small,
    category: "staffing",
    read: rankableStaff.length < 2
      ? `Kept rate per person is a ratio, and this window leaves fewer than two staff with ${MIN_BOOKED} bookings or more. Widen the range or the location filter to read it.`
      : `${Math.round(keptRate(hi) * 100)}% of ${staffName(hi, 0)}'s appointments are kept against ${Math.round(keptRate(lo) * 100)}% of ${staffName(lo, 1)}'s. Whether an appointment is kept is mostly the student's action and the reminder's — read this as a question about slot times and caseload, never as a ranking of people.`,
    impact: rankableStaff.length >= 2
      ? `${lo.booked - lo.kept} of ${staffName(lo, 1)}'s ${lo.booked} bookings did not happen.`
      : "Not measurable at this size.",
    action: rankableStaff.length >= 2
      ? "Compare the two schedules before the two people: time of day, lead time and how the slots were booked."
      : "Nothing to route at this size.",
    owner: "You, with advising ops.",
    method: `Kept ÷ booked per staff member, staff with ${MIN_BOOKED}+ bookings only, ${win}. Not adjusted for time of day, service or lead time — the three things most likely to explain it.`,
    rows: rankableStaff.slice(0, 3).map((s, i) => ({ k: staffName(s, i), v: `${Math.round(keptRate(s) * 100)}% of ${s.booked}` })),
    rowsAffected: rankableStaff, kind: "staff",
    verb: null,
  });

  /* 6 · Excluded rows ─────────────────────────────────────────────────
   * The service and staff tables do not have to add up to the totals: an
   * appointment with no service or no staff recorded lands in neither. Today
   * that difference is invisible; here it is a finding. */
  const svcSum = svc.reduce((a, s) => a + s.booked, 0);
  const staffSum = staff.reduce((a, s) => a + s.booked, 0);
  const noSvc = Math.max(0, k.booked - svcSum);
  const noStaff = Math.max(0, k.booked - staffSum);
  if (noSvc > 0 || noStaff > 0) {
    const worstShare = Math.max(share(noSvc, k.booked), share(noStaff, k.booked));
    out.push({
      id: "unattributed", index: "6",
      value: Math.max(noSvc, noStaff).toLocaleString(), unit: "bookings unattributed",
      headline: "are missing from a table below",
      severity: worstShare >= 0.1 ? "medium" : "low", note: null,
      category: "data",
      read: `The tables below do not have to sum to the totals above, and they do not: ${noSvc.toLocaleString()} bookings carry no service and ${noStaff.toLocaleString()} carry no staff member. They are counted in every rate at the top and in neither breakdown.`,
      impact: `Per-service and per-staff figures describe ${(k.booked - Math.max(noSvc, noStaff)).toLocaleString()} of ${k.booked.toLocaleString()} bookings.`,
      action: "Find where these are created without a service or an owner — most often an imported or a phone booking — and require both at the point of entry.",
      owner: "Advising ops, with whoever owns the intake path.",
      method: "Booked total minus the sum of the per-service and per-staff rows the report returns.",
      rows: [
        { k: "No service recorded", v: noSvc.toLocaleString() },
        { k: "No staff recorded", v: noStaff.toLocaleString() },
        { k: "Booked in total", v: k.booked.toLocaleString() },
      ],
      rowsAffected: [], kind: "none",
      verb: null,
    });
  }
  return out;
}
